// Pestanya "Absències" de Gestió d'un Empleat: calendari amb navegació,
// selecció de dia/rang a l'estil "aerolínies" (CURRENT_TASK.md, punts 5-6),
// i creació/modificació/esborrat d'Absències. Dues vistes: Mes (per defecte)
// o Any complet (els 12 mesos de cop), per a rangs amplis.
//
// Reutilitza les utilitats de calendari/hores ja fetes per HorariEditor.js
// (NOMS_MESOS, DIES_SETMANA, generarDiesMes, formatDataISO, formatHoraCurta)
// en comptes de duplicar-les.

import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { fetchAutenticat, SessioCaducadaError } from "../../utils/auth";
import { formatDate } from "../../utils/Utilitats";
import TeclaEscEnter from "../../utils/Teclaescenter";
import { API_URL } from "../../utils/config";
import { NOMS_MESOS, DIES_SETMANA, generarDiesMes, formatDataISO, formatHoraCurta } from "./HorariEditor";
import "./absenciesempleat.css";

const MOSTRAR_OPCIONS = [
  { valor: "totes", nom: "Totes" },
  { valor: "actives", nom: "Actives" },
  { valor: "historiques", nom: "Històriques" },
];

const ESTAT_OPCIONS = [
  { valor: "PENDENT", nom: "Pendent" },
  { valor: "JUSTIFICADA", nom: "Justificada" },
  { valor: "NO_JUSTIFICADA", nom: "No justificada" },
];

function AbsenciesEmpleat({ idEmpleat, idEmpresa }) {
  const avui = new Date();
  const [any, setAny] = useState(avui.getFullYear());
  const [mes, setMes] = useState(avui.getMonth()); // 0-11
  const [vista, setVista] = useState("mes"); // "mes" | "any"

  const [absencies, setAbsencies] = useState([]);
  const [tipusOptions, setTipusOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [filtreTipus, setFiltreTipus] = useState("");
  const [filtreEstat, setFiltreEstat] = useState("");
  const [filtreDiaComplet, setFiltreDiaComplet] = useState("");
  const [filtreMostrar, setFiltreMostrar] = useState("totes");

  // Selecció de dia/rang a l'estil "aerolínies", igual que SelectorRangDates
  // (HorariEditor.js) però es manté en canviar de mes/any/vista (CURRENT_TASK.md, punt 5).
  const [seleccio, setSeleccio] = useState(null); // { inici, final }
  const [hover, setHover] = useState(null);

  const [formCrear, setFormCrear] = useState(null);
  const [enviantCrear, setEnviantCrear] = useState(false);
  const [errorCrear, setErrorCrear] = useState("");

  const [edicio, setEdicio] = useState(null);
  const [formEdicio, setFormEdicio] = useState(null);
  const [enviantEdicio, setEnviantEdicio] = useState(false);
  const [errorEdicio, setErrorEdicio] = useState("");

  // El formulari de Crear/Modificar apareix sota el calendari: quan s'obre
  // (per exemple en clicar el xip d'una Absència existent), el portem a la
  // vista automàticament perquè no quedi amagat sota el calendari/llegenda
  // sense fer scroll.
  const formCrearRef = useRef(null);
  const formEdicioRef = useRef(null);
  useEffect(() => {
    if (formCrear && formCrearRef.current) {
      formCrearRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [formCrear]);
  useEffect(() => {
    if (edicio && formEdicioRef.current) {
      formEdicioRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [edicio]);

  const carregarTipus = useCallback(async () => {
    if (!idEmpresa) return;
    try {
      const res = await fetchAutenticat(`${API_URL}/TipusAbsencia.php?idEmpresa=${idEmpresa}`);
      const data = await res.json();
      if (data.success) setTipusOptions(data.tipus || []);
    } catch (e) {
      if (!(e instanceof SessioCaducadaError)) console.error("Error carregant Tipus d'absència:", e);
    }
  }, [idEmpresa]);

  // Carrega totes les Absències de l'Any visible (sempre l'any complet, mai
  // només el Mes) una sola vegada des del backend. Així:
  // - navegar de Mes dins el mateix Any, o
  // - canviar entre vista "Mes"/"Any complet",
  // no torna a demanar dades al servidor (ja les tenim totes en memòria).
  // Només canviar d'Any dispara una petició nova. Els filtres
  // (Tipus/Estat/Durada/Mostrar) tampoc hi disparen peticions: s'apliquen en
  // memòria (useMemo més avall).
  const carregarAbsencies = useCallback(async () => {
    if (!idEmpleat || !idEmpresa) return;
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({ idEmpleat, idEmpresa, Any: any });

      const res = await fetchAutenticat(`${API_URL}/AbsenciesEmpleat.php?${params.toString()}`);
      const data = await res.json();
      if (data.success) {
        setAbsencies(data.absencies || []);
      } else {
        setError(data.message || "No s'han pogut carregar les Absències.");
        setAbsencies([]);
      }
    } catch (e) {
      if (!(e instanceof SessioCaducadaError)) setError("Error de connexió amb el servidor.");
    } finally {
      setLoading(false);
    }
  }, [idEmpleat, idEmpresa, any]);

  useEffect(() => {
    carregarTipus();
  }, [carregarTipus]);

  useEffect(() => {
    carregarAbsencies();
  }, [carregarAbsencies]);

  // Data d'avui en local (no UTC, per evitar desquadres de dia a prop de
  // mitjanit), amb el mateix format "Y-m-d" que fa servir la resta de dates.
  const avuiISO = useMemo(() => {
    const ara = new Date();
    return formatDataISO(ara.getFullYear(), ara.getMonth(), ara.getDate());
  }, []);

  const absenciesFiltrades = useMemo(() => {
    return absencies.filter((a) => {
      if (filtreTipus && String(a.TipusAbsenciaEmpleats_idTipusAbsenciaEmpleats) !== String(filtreTipus)) return false;
      if (filtreEstat && a.EstatJustificacio !== filtreEstat) return false;
      if (filtreDiaComplet !== "" && String(Number(a.EsDiaComplet)) !== String(filtreDiaComplet)) return false;
      if (filtreMostrar === "actives" && a.Data < avuiISO) return false;
      if (filtreMostrar === "historiques" && a.Data >= avuiISO) return false;
      return true;
    });
  }, [absencies, filtreTipus, filtreEstat, filtreDiaComplet, filtreMostrar, avuiISO]);

  const absenciesPerDia = useMemo(() => {
    const mapa = {};
    absenciesFiltrades.forEach((a) => {
      if (!mapa[a.Data]) mapa[a.Data] = [];
      mapa[a.Data].push(a);
    });
    return mapa;
  }, [absenciesFiltrades]);

  // Tipus vàlids per donar-se d'alta en una Absència ordinària (actius i no
  // marcats com a Baixa laboral: aquests es gestionen des de la pestanya
  // Baixes, CURRENT_TASK.md punt 14).
  const tipusValidsCreacio = useMemo(
    () => tipusOptions.filter((t) => Number(t.Actiu) === 1 && Number(t.EsBaixaLaboral) !== 1),
    [tipusOptions]
  );

  const canviarMes = (delta) => {
    let novaMes = mes + delta;
    let novaAny = any;
    if (novaMes < 0) {
      novaMes = 11;
      novaAny -= 1;
    } else if (novaMes > 11) {
      novaMes = 0;
      novaAny += 1;
    }
    setMes(novaMes);
    setAny(novaAny);
  };

  const navegarAnterior = () => (vista === "mes" ? canviarMes(-1) : setAny((a) => a - 1));
  const navegarSeguent = () => (vista === "mes" ? canviarMes(1) : setAny((a) => a + 1));

  const tancarPanells = useCallback(() => {
    setSeleccio(null);
    setFormCrear(null);
    setErrorCrear("");
    setEdicio(null);
    setFormEdicio(null);
    setErrorEdicio("");
  }, []);

  TeclaEscEnter(tancarPanells, null);

  // El Tipus del filtre principal (si n'hi ha un vàlid seleccionat) queda
  // preseleccionat al formulari de Nova Absència: és el cas d'ús més
  // habitual (filtrar per "Vacances" i després crear-ne una de noves).
  const iniciarCreacioForm = () => {
    const tipusDelFiltre = filtreTipus
      ? tipusValidsCreacio.find((t) => String(t.idTipusAbsenciaEmpleats) === String(filtreTipus))
      : null;
    const tipusPerDefecte = tipusDelFiltre || tipusValidsCreacio[0];
    setFormCrear({
      idTipus: tipusPerDefecte?.idTipusAbsenciaEmpleats || "",
      esDiaComplet: true,
      horaInici: "",
      horaFinal: "",
      esRecuperable: tipusPerDefecte ? Number(tipusPerDefecte.RecuperablePerDefecte) === 1 : true,
      notes: "",
    });
    setErrorCrear("");
  };

  const handleClickDia = (dataISO) => {
    setEdicio(null);
    setFormEdicio(null);

    if (!seleccio || seleccio.final) {
      setSeleccio({ inici: dataISO, final: null });
      setFormCrear(null);
      return;
    }
    if (dataISO < seleccio.inici) {
      setSeleccio({ inici: dataISO, final: null });
      setFormCrear(null);
      return;
    }
    setSeleccio({ inici: seleccio.inici, final: dataISO });
    iniciarCreacioForm();
  };

  const handleClickChip = (absencia, e) => {
    e.stopPropagation();
    setSeleccio(null);
    setFormCrear(null);
    setEdicio(absencia);
    setFormEdicio({
      idTipus: absencia.TipusAbsenciaEmpleats_idTipusAbsenciaEmpleats,
      estatJustificacio: absencia.EstatJustificacio,
      esDiaComplet: Number(absencia.EsDiaComplet) === 1,
      horaInici: absencia.HoraInici ? absencia.HoraInici.slice(0, 5) : "",
      horaFinal: absencia.HoraFinal ? absencia.HoraFinal.slice(0, 5) : "",
      esRecuperable: Number(absencia.EsRecuperable) === 1,
      notes: absencia.Notes || "",
    });
    setErrorEdicio("");
  };

  const handleCanviTipusCrear = (idTipus) => {
    const tipus = tipusOptions.find((t) => String(t.idTipusAbsenciaEmpleats) === String(idTipus));
    setFormCrear((prev) => ({
      ...prev,
      idTipus,
      esRecuperable: tipus ? Number(tipus.RecuperablePerDefecte) === 1 : prev.esRecuperable,
    }));
  };

  const handleCrearAbsencia = async () => {
    if (!seleccio?.inici || !formCrear) return;
    if (!formCrear.idTipus) {
      setErrorCrear("Selecciona un Tipus d'absència.");
      return;
    }
    if (!formCrear.esDiaComplet && (!formCrear.horaInici || !formCrear.horaFinal)) {
      setErrorCrear("Indica l'Hora d'inici i l'Hora final.");
      return;
    }
    setEnviantCrear(true);
    setErrorCrear("");
    try {
      const res = await fetchAutenticat(`${API_URL}/NovaAbsenciaEmpleat.php`, {
        method: "POST",
        body: JSON.stringify({
          idEmpresa,
          idEmpleat,
          DataInici: seleccio.inici,
          DataFinal: seleccio.final || seleccio.inici,
          TipusAbsenciaEmpleats_idTipusAbsenciaEmpleats: formCrear.idTipus,
          EsDiaComplet: formCrear.esDiaComplet,
          HoraInici: formCrear.esDiaComplet ? null : formCrear.horaInici,
          HoraFinal: formCrear.esDiaComplet ? null : formCrear.horaFinal,
          EsRecuperable: formCrear.esRecuperable,
          Notes: formCrear.notes || null,
        }),
      });
      const data = await res.json();
      if (data.success) {
        let missatge = data.message || "Absència creada correctament.";
        if (data.ajustos && data.ajustos.length > 0) {
          missatge += "\n\nHores ajustades a la jornada prevista:\n" + data.ajustos.map((o) => `- ${formatDate(o.Data)}: ${o.Motiu}`).join("\n");
        }
        if (data.omesos && data.omesos.length > 0) {
          missatge += "\n\nDies omesos:\n" + data.omesos.map((o) => `- ${formatDate(o.Data)}: ${o.Motiu}`).join("\n");
        }
        alert(missatge);
        tancarPanells();
        carregarAbsencies();
      } else {
        setErrorCrear(data.message || "No s'ha pogut crear l'Absència.");
      }
    } catch (e) {
      if (!(e instanceof SessioCaducadaError)) setErrorCrear("Error de connexió amb el servidor.");
    } finally {
      setEnviantCrear(false);
    }
  };

  const handleModificarAbsencia = async () => {
    if (!edicio || !formEdicio) return;
    if (!formEdicio.esDiaComplet && (!formEdicio.horaInici || !formEdicio.horaFinal)) {
      setErrorEdicio("Indica l'Hora d'inici i l'Hora final.");
      return;
    }
    setEnviantEdicio(true);
    setErrorEdicio("");
    try {
      const res = await fetchAutenticat(`${API_URL}/ModificarAbsenciaEmpleat.php`, {
        method: "POST",
        body: JSON.stringify({
          idEmpresa,
          idAbsenciesEmpleats: edicio.idAbsenciesEmpleats,
          TipusAbsenciaEmpleats_idTipusAbsenciaEmpleats: formEdicio.idTipus,
          EstatJustificacio: formEdicio.estatJustificacio,
          EsDiaComplet: formEdicio.esDiaComplet,
          HoraInici: formEdicio.esDiaComplet ? null : formEdicio.horaInici,
          HoraFinal: formEdicio.esDiaComplet ? null : formEdicio.horaFinal,
          EsRecuperable: formEdicio.esRecuperable,
          Notes: formEdicio.notes || null,
        }),
      });
      const data = await res.json();
      if (data.success) {
        tancarPanells();
        carregarAbsencies();
        if (data.ajustat) alert(data.message);
      } else {
        setErrorEdicio(data.message || "No s'ha pogut modificar l'Absència.");
      }
    } catch (e) {
      if (!(e instanceof SessioCaducadaError)) setErrorEdicio("Error de connexió amb el servidor.");
    } finally {
      setEnviantEdicio(false);
    }
  };

  const handleEsborrarAbsencia = async () => {
    if (!edicio) return;
    if (!window.confirm(`Esborrar l'Absència del ${formatDate(edicio.Data)}?`)) return;
    try {
      const res = await fetchAutenticat(`${API_URL}/EsborrarAbsenciaEmpleat.php`, {
        method: "POST",
        body: JSON.stringify({ idEmpresa, idAbsenciesEmpleats: edicio.idAbsenciesEmpleats }),
      });
      const data = await res.json();
      if (data.success) {
        tancarPanells();
        carregarAbsencies();
      } else {
        alert(data.message || "No s'ha pogut esborrar l'Absència.");
      }
    } catch (e) {
      if (!(e instanceof SessioCaducadaError)) alert("Error de connexió amb el servidor.");
    }
  };

  if (!idEmpleat || !idEmpresa) {
    return <p>Falten dades (Empleat o Empresa) per carregar les Absències.</p>;
  }

  const finalEfectiu = seleccio && !seleccio.final && hover && hover >= seleccio.inici ? hover : seleccio?.final;

  // Una cel·la de dia: en vista "any" es mostra en format compacte (punts de
  // color en comptes de xips de text), perquè hi càpiguen els 12 mesos de cop.
  const renderDiaCella = (anyCel, mesCel, dia, index, compacte) => {
    if (dia === null) {
      return (
        <div
          key={index}
          className={`absencies-emp-dia absencies-emp-dia-buit${compacte ? " absencies-emp-dia-petit" : ""}`}
        />
      );
    }
    const dataISO = formatDataISO(anyCel, mesCel, dia);
    const absenciesDia = absenciesPerDia[dataISO] || [];
    const esExtrem = seleccio && (dataISO === seleccio.inici || dataISO === seleccio.final);
    const enRang = seleccio && finalEfectiu && dataISO > seleccio.inici && dataISO < finalEfectiu;
    const classes = ["absencies-emp-dia"];
    if (compacte) classes.push("absencies-emp-dia-petit");
    if (esExtrem) classes.push("absencies-emp-dia-extrem");
    if (enRang) classes.push("absencies-emp-dia-rang");

    return (
      <div
        key={index}
        className={classes.join(" ")}
        onClick={() => handleClickDia(dataISO)}
        onMouseEnter={() => setHover(dataISO)}
      >
        <div className="absencies-emp-dia-num">{dia}</div>
        {compacte ? (
          absenciesDia.length > 0 && (
            <div className="absencies-emp-dia-punts">
              {absenciesDia.slice(0, 4).map((a) => (
                <span
                  key={a.idAbsenciesEmpleats}
                  className="absencies-emp-punt"
                  style={{ backgroundColor: a.TipusColor || "#999999" }}
                  title={`${a.TipusNom} (clica per modificar o esborrar)`}
                  onClick={(e) => handleClickChip(a, e)}
                />
              ))}
            </div>
          )
        ) : (
          <div className="absencies-emp-dia-chips">
            {absenciesDia.slice(0, 3).map((a) => (
              <span
                key={a.idAbsenciesEmpleats}
                className="absencies-emp-chip"
                style={{ backgroundColor: a.TipusColor || "#999999" }}
                title={`${a.TipusNom}${Number(a.EsDiaComplet) === 1 ? "" : ` (${formatHoraCurta(a.HoraInici)}–${formatHoraCurta(a.HoraFinal)})`} — clica per modificar o esborrar`}
                onClick={(e) => handleClickChip(a, e)}
              >
                {a.TipusCodi || a.TipusNom}
              </span>
            ))}
            {absenciesDia.length > 3 && (
              <span className="absencies-emp-chip-mes">+{absenciesDia.length - 3}</span>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderGraellaMes = (anyMes, mesIdx, compacte) => (
    <div className={`absencies-emp-mes-bloc${compacte ? " absencies-emp-mes-bloc-petit" : ""}`} key={`${anyMes}-${mesIdx}`}>
      {compacte && <div className="absencies-emp-mes-titol">{NOMS_MESOS[mesIdx]}</div>}
      <div className="absencies-emp-dies-setmana">
        {DIES_SETMANA.map((d) => (
          <div key={d.valor} className="absencies-emp-dia-setmana">{d.curt}</div>
        ))}
      </div>
      <div className="absencies-emp-graella">
        {generarDiesMes(anyMes, mesIdx).map((dia, index) => renderDiaCella(anyMes, mesIdx, dia, index, compacte))}
      </div>
    </div>
  );

  return (
    <div className="absencies-emp">
      <div className="absencies-emp-filtres">
        <label>
          Tipus:
          <select value={filtreTipus} onChange={(e) => setFiltreTipus(e.target.value)}>
            <option value="">Tots</option>
            {tipusOptions.map((t) => (
              <option key={t.idTipusAbsenciaEmpleats} value={t.idTipusAbsenciaEmpleats}>{t.Nom}</option>
            ))}
          </select>
        </label>
        <label>
          Estat justificació:
          <select value={filtreEstat} onChange={(e) => setFiltreEstat(e.target.value)}>
            <option value="">Tots</option>
            {ESTAT_OPCIONS.map((o) => (
              <option key={o.valor} value={o.valor}>{o.nom}</option>
            ))}
          </select>
        </label>
        <label>
          Durada:
          <select value={filtreDiaComplet} onChange={(e) => setFiltreDiaComplet(e.target.value)}>
            <option value="">Totes</option>
            <option value="1">Dia complet</option>
            <option value="0">Parcial</option>
          </select>
        </label>
        <label>
          Mostrar:
          <select value={filtreMostrar} onChange={(e) => setFiltreMostrar(e.target.value)}>
            {MOSTRAR_OPCIONS.map((o) => (
              <option key={o.valor} value={o.valor}>{o.nom}</option>
            ))}
          </select>
        </label>
      </div>

      <div className="absencies-emp-vista-selector">
        <button type="button" className={vista === "mes" ? "active" : ""} onClick={() => setVista("mes")}>Mes</button>
        <button type="button" className={vista === "any" ? "active" : ""} onClick={() => setVista("any")}>Any complet</button>
      </div>

      <div className="absencies-emp-nav">
        <button type="button" className="dv2-btn dv2-btn--icon" onClick={navegarAnterior}>‹</button>
        <span className="absencies-emp-titol-mes">{vista === "mes" ? `${NOMS_MESOS[mes]} ${any}` : any}</span>
        <button type="button" className="dv2-btn dv2-btn--icon" onClick={navegarSeguent}>›</button>
      </div>

      <p className="absencies-emp-ajuda">
        Clica un dia buit (o arrossega dos dies) per crear-hi una nova Absència. Clica l'etiqueta de color d'una Absència ja existent per modificar-la o esborrar-la.
      </p>

      {loading && <p>Carregant Absències...</p>}
      {error && <p className="absencies-emp-error">{error}</p>}

      {!loading && !error && (
        <div className="absencies-emp-calendari" onMouseLeave={() => setHover(null)}>
          {vista === "mes" ? (
            renderGraellaMes(any, mes, false)
          ) : (
            <div className="absencies-emp-any-graella">
              {NOMS_MESOS.map((_, idx) => renderGraellaMes(any, idx, true))}
            </div>
          )}
        </div>
      )}

      {seleccio && (
        <div className="absencies-emp-resum-seleccio">
          {seleccio.final ? (
            <span>Selecció: <strong>{formatDate(seleccio.inici)}</strong> — <strong>{formatDate(seleccio.final)}</strong></span>
          ) : (
            <span>
              Inici: <strong>{formatDate(seleccio.inici)}</strong> — selecciona ara la data final (o torna a clicar el mateix dia per una absència d'un sol dia)
            </span>
          )}{" "}
          <button type="button" className="Dashboard-btn Dashboard-btn-cancelar absencies-emp-btn-petit" onClick={tancarPanells}>
            Cancel·lar selecció
          </button>
        </div>
      )}

      {formCrear && (
        <div className="Dashboard-form-section absencies-emp-form" ref={formCrearRef}>
          <h4>
            Nova Absència ({formatDate(seleccio.inici)}
            {seleccio.final && seleccio.final !== seleccio.inici ? ` – ${formatDate(seleccio.final)}` : ""})
          </h4>
          <div className="Dashboard-form-row">
            <label>
              Tipus:
              <select value={formCrear.idTipus} onChange={(e) => handleCanviTipusCrear(e.target.value)}>
                <option value="">Selecciona un Tipus</option>
                {tipusValidsCreacio.map((t) => (
                  <option key={t.idTipusAbsenciaEmpleats} value={t.idTipusAbsenciaEmpleats}>{t.Nom}</option>
                ))}
              </select>
            </label>
            <label className="checkbox-label">
              Dia complet:
              <input
                type="checkbox"
                checked={formCrear.esDiaComplet}
                onChange={(e) => setFormCrear((p) => ({ ...p, esDiaComplet: e.target.checked }))}
              />
            </label>
            <label className="checkbox-label">
              Recuperable:
              <input
                type="checkbox"
                checked={formCrear.esRecuperable}
                onChange={(e) => setFormCrear((p) => ({ ...p, esRecuperable: e.target.checked }))}
              />
            </label>
          </div>

          {!formCrear.esDiaComplet && (
            <div className="Dashboard-form-row">
              <label>
                Hora inici:
                <input type="time" value={formCrear.horaInici} onChange={(e) => setFormCrear((p) => ({ ...p, horaInici: e.target.value }))} />
              </label>
              <label>
                Hora final:
                <input type="time" value={formCrear.horaFinal} onChange={(e) => setFormCrear((p) => ({ ...p, horaFinal: e.target.value }))} />
              </label>
              <span className="absencies-emp-avis-hores">
                Si les hores indicades superen la jornada prevista aquest dia, s'ajustaran automàticament.
              </span>
            </div>
          )}

          <div className="Dashboard-form-row">
            <label>
              Notes:
              <textarea value={formCrear.notes} onChange={(e) => setFormCrear((p) => ({ ...p, notes: e.target.value }))} maxLength="255" />
            </label>
          </div>

          {errorCrear && <p className="absencies-emp-error">{errorCrear}</p>}

          <div className="Dashboard-button-group">
            <button type="button" className="Dashboard-btn Dashboard-btn-acceptar" onClick={handleCrearAbsencia} disabled={enviantCrear}>
              Crear
            </button>
            <button type="button" className="Dashboard-btn Dashboard-btn-cancelar" onClick={tancarPanells} disabled={enviantCrear}>
              Cancel·lar
            </button>
          </div>
        </div>
      )}

      {edicio && formEdicio && (
        <div className="Dashboard-form-section absencies-emp-form" ref={formEdicioRef}>
          <h4>Absència del {formatDate(edicio.Data)}</h4>
          <div className="Dashboard-form-row">
            <label>
              Tipus:
              <select value={formEdicio.idTipus} onChange={(e) => setFormEdicio((p) => ({ ...p, idTipus: e.target.value }))}>
                {tipusOptions
                  .filter((t) => Number(t.EsBaixaLaboral) !== 1 || String(t.idTipusAbsenciaEmpleats) === String(formEdicio.idTipus))
                  .map((t) => (
                    <option key={t.idTipusAbsenciaEmpleats} value={t.idTipusAbsenciaEmpleats}>{t.Nom}</option>
                  ))}
              </select>
            </label>
            <label>
              Estat justificació:
              <select value={formEdicio.estatJustificacio} onChange={(e) => setFormEdicio((p) => ({ ...p, estatJustificacio: e.target.value }))}>
                {ESTAT_OPCIONS.map((o) => (
                  <option key={o.valor} value={o.valor}>{o.nom}</option>
                ))}
              </select>
            </label>
            <label className="checkbox-label">
              Dia complet:
              <input
                type="checkbox"
                checked={formEdicio.esDiaComplet}
                onChange={(e) => setFormEdicio((p) => ({ ...p, esDiaComplet: e.target.checked }))}
              />
            </label>
            <label className="checkbox-label">
              Recuperable:
              <input
                type="checkbox"
                checked={formEdicio.esRecuperable}
                onChange={(e) => setFormEdicio((p) => ({ ...p, esRecuperable: e.target.checked }))}
              />
            </label>
          </div>

          {!formEdicio.esDiaComplet && (
            <div className="Dashboard-form-row">
              <label>
                Hora inici:
                <input type="time" value={formEdicio.horaInici} onChange={(e) => setFormEdicio((p) => ({ ...p, horaInici: e.target.value }))} />
              </label>
              <label>
                Hora final:
                <input type="time" value={formEdicio.horaFinal} onChange={(e) => setFormEdicio((p) => ({ ...p, horaFinal: e.target.value }))} />
              </label>
              <span className="absencies-emp-avis-hores">
                Si les hores indicades superen la jornada prevista aquest dia, s'ajustaran automàticament.
              </span>
            </div>
          )}

          <div className="Dashboard-form-row">
            <label>
              Notes:
              <textarea value={formEdicio.notes} onChange={(e) => setFormEdicio((p) => ({ ...p, notes: e.target.value }))} maxLength="255" />
            </label>
          </div>

          {errorEdicio && <p className="absencies-emp-error">{errorEdicio}</p>}

          <div className="Dashboard-button-group">
            <button type="button" className="Dashboard-btn Dashboard-btn-acceptar" onClick={handleModificarAbsencia} disabled={enviantEdicio}>
              Modificar
            </button>
            <button type="button" className="Dashboard-btn absencies-emp-btn-esborrar" onClick={handleEsborrarAbsencia} disabled={enviantEdicio}>
              Esborrar
            </button>
            <button type="button" className="Dashboard-btn Dashboard-btn-cancelar" onClick={tancarPanells} disabled={enviantEdicio}>
              Tancar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default AbsenciesEmpleat;
